import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  ExpandableSearch,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useWidgetViewState,
  type Omittable,
} from "@salestrak/ui";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { CustomFieldDef } from "#app/components/issue-view/issue-view-types";
import {
  getAttachedFieldIds,
  SummaryAddCustomFieldsPicker,
} from "./add-custom-fields-picker";

/**
 * The Custom fields module's header controls, shared by the Project, Account
 * and Contact summaries: the expandable search (over the kit's
 * `useWidgetViewState` channel, which the module body reads back), and the
 * add-field plus menu (Create field… / Add field from workspace…, opening the
 * shared picker). The host wrapper resolves the field bag from its own context
 * and gates `canAddFields` — read-only viewers and the Project create flow hide
 * the menu, the search stays.
 *
 * These render only while the section HAS fields: an empty section is the
 * body's `SummaryCustomFieldsEmptyAction`, which the card lifts into the header
 * in this row's place.
 */
export function SummaryCustomFieldsHeaderActions({
  values,
  orderedTypeFieldIds,
  customFields,
  onUpdateValue,
  canAddFields,
  locationName,
}: Omittable<{
  values?: Record<string, unknown>;
  orderedTypeFieldIds?: string[];
  customFields?: CustomFieldDef[];
  /** Adding a field writes its type default through this — the same door every
   *  row edit takes. */
  onUpdateValue?: (data: { customFieldId: string; value: unknown }) => void;
  canAddFields: boolean;
  /** Names the scope in the add-fields picker header. */
  locationName?: string;
}>) {
  const { state, patch } = useWidgetViewState();
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const canManageCustomFields = usePermission("custom_fields.manage");
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const searchValue = typeof state.search === "string" ? state.search : "";
  const fieldIds = useMemo(
    () => getAttachedFieldIds({ values, orderedTypeFieldIds }),
    [values, orderedTypeFieldIds],
  );
  const hasFields = fieldIds.length > 0;

  const handleSearchChange = useCallback(
    (value: string) => {
      patch({ search: value });
    },
    [patch],
  );

  return (
    <Box direction="row" align="center" gap="xs">
      {hasFields ? (
        <ExpandableSearch
          size="sm"
          value={searchValue}
          onValueChange={handleSearchChange}
          placeholder="Search fields..."
          tooltip="Search fields"
        />
      ) : null}
      {canAddFields ? (
        <Menu>
          {/* MenuTrigger's prop set is closed, so the TooltipTrigger sits
              INSIDE its render chain — the tooltip's hover handlers merge
              down onto the button element itself. */}
          <Tooltip>
            <MenuTrigger
              render={
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Add a field"
                    />
                  }
                />
              }
            >
              <Icon name="plus" size="md" />
            </MenuTrigger>
            <TooltipPopup side="top">Add field</TooltipPopup>
          </Tooltip>
          <MenuPopup align="end">
            {canManageCustomFields ? (
              <MenuItem onClick={() => openCustomFieldsDialog()}>
                <Icon name="plus" size="md" />
                <Text>Create field...</Text>
              </MenuItem>
            ) : null}
            <MenuItem onClick={() => setAddFieldDialogOpen(true)}>
              <Icon name="book-open" size="md" />
              <Text>Add field from workspace...</Text>
            </MenuItem>
          </MenuPopup>
        </Menu>
      ) : null}

      {canAddFields ? (
        <SummaryAddCustomFieldsPicker
          open={addFieldDialogOpen}
          onOpenChange={setAddFieldDialogOpen}
          fieldIds={fieldIds}
          customFields={customFields}
          onUpdateValue={onUpdateValue}
          locationName={locationName}
        />
      ) : null}
    </Box>
  );
}
